import { isInstanceOf, isObject, isString } from "./types.js";
import { isArray } from "./array.js";
/**
 * Debugging Service using two labels.
 * - ASSERT
 * - THROW_ASSERT
 *
 * Statements labeled in this way are expected to be dropped in code built for production.
 *
 * @todo rename into typeguard.js
 * @todo Throw TypeguardError, TypeguardExcessError extends TypeguardError
 * @module
 */
ASSERT: console.info(`Build includes "ASSERT" labeled statements.
Those should print a warning whenever an assertion fails.`);
/**
 * @todo Rename label into TYPEGUARD_THROW.
 */
THROW_ASSERT: console.warn(`Build includes "THROW_ASSERT" labeled statements!
Those should throw an exception when an assertion fails.`);
const CAPTURE = Symbol();
const CONTEXT = Symbol();
/**
 * @typedef {{
 *   [CAPTURE]?: boolean; // CAPTURE
 *   [CONTEXT]?: unknown;
 * }} Validator
 */
/**
 * @template T
 * @callback Assertion
 * @param {string} name
 * @param {unknown} value
 * @param {Validator} [validator]
 * @returns {asserts value is T}
 */
/**
 * @template T
 * @typedef {{(name: string, value: unknown, validator?: Validator): asserts value is T, explain?: string}} Typeguard
 */
/**
 * @template T
 * @typedef {T extends Assertion<infer F> ? F : never} Asserted
 */
/**
 * @template T
 * @param {string} name
 * @param {unknown} value
 * @param {Assertion<T>} assertion
 * @param {Validator} [validator]
 * @returns {asserts value is T}
 */
export function typeguard(name, value, assertion, validator) {
    assertion(name, value, validator);
}
/**
 * @deprecated use `typeguard`
 * @type {typeof typeguard}
 */
export const assert = typeguard;
/**
 * @param {string} name
 * @param {Record<string, any>} data
 */
export function validator(name, data) {
    typeguard(name, data, RECORD(ANY, ANY));
    /** @type {Validator} */
    const validator = { [CONTEXT]: data };
    /**
     * @template T
     * @param {string} key
     * @param {Assertion<T>} assertion
     * @returns {T}
     */
    const get = (key, assertion) => (assertion(`${name}["${key}"]`, data[key], validator), data[key]);
    /**
     * @alpha
     */
    get.force = () => ((validator[CAPTURE] = true), get);
    /**
     * @param {Function} constructor
     * @param {Object} object
     */
    get.finish = (constructor, object) => {
        FULLY_ASSIGNED(object, data);
        FINAL_CLASS(constructor, object);
    };
    return get;
}
/**
 * @param {string} message
 * @param {unknown} value
 * @param {Validator} [validator]
 * @returns {never}
 */
export function fail(message, value, validator) {
    if (validator?.[CAPTURE]) {
        throw Error(message);
    }
    console.warn(message, { value, context: validator?.[CONTEXT] });
    THROW_ASSERT: {
        throw Error(message);
    }
}
/**
 * @template T
 * @param {Typeguard<T>} typeguard
 */
export function OPTIONAL(typeguard) {
    /** @type {Typeguard<T | undefined>} */
    const fn = (name, value, validator) => {
        if (value !== undefined) {
            typeguard(name, value, validator);
        }
    };
    fn.explain = `OPTIONAL(${typeguard.explain ?? typeguard.name})`;
    return fn;
}
/**
 * @template T
 * @param {Typeguard<T>} typeguard
 */
export function NULLABLE(typeguard) {
    /** @type {Typeguard<T | null>} */
    const fn = (name, value, validator) => {
        if (value !== null) {
            typeguard(name, value, validator);
        }
    };
    fn.explain = `NULLABLE(${typeguard.explain ?? typeguard.name})`;
    return fn;
}
/**
 * @template T
 * @param {number} min
 * @param {number} max
 * @param {Assertion<T>} assertion
 * @returns {Assertion<T>}
 */
export function MINMAX(min, max, assertion) {
    return (name, value, validator) => {
        assertion(name, value, validator);
        let length;
        if (isArray(value) || isString(value)) {
            length = value.length;
        }
        else if (typeof value === "number") {
            length = value;
        }
        else if (isObject(value)) {
            length = Object.keys(value).length;
        }
        else {
            fail(`${name} has no length`, value, validator);
        }
        if (length < min) {
            fail(`${name} is smaller than ${min}`, value, validator);
        }
        if (length > max) {
            fail(`${name} is larger than ${max}`, value, validator);
        }
    };
}
/**
 * @template T
 * @param {number} min
 * @param {Assertion<T>} assertion
 * @returns {Assertion<T>}
 */
export function MIN(min, assertion) {
    return (name, value, validator) => typeguard(name, value, MINMAX(min, Infinity, assertion), validator);
}
/**
 * @template T
 * @param {number} max
 * @param {Assertion<T>} assertion
 * @returns {Assertion<T>}
 */
export function MAX(max, assertion) {
    return (name, value, validator) => typeguard(name, value, MINMAX(-Infinity, max, assertion), validator);
}
/**
 * @template {Object} T
 * @param {new() => T} constructor
 * @returns {Assertion<T>}
 */
export function IS(constructor) {
    return (name, value, validator) => {
        if (!isInstanceOf(value, constructor)) {
            fail(`${name} is not a ${constructor.name}`, value, validator);
        }
    };
}
/**
 * @template T
 * @param {Typeguard<T>} typeguard
 */
export function ARRAY(typeguard) {
    /** @type {Typeguard<T[]>} */
    const fn = (name, value, validator) => {
        if (!Array.isArray(value)) {
            fail(`${name} is not an array`, value, validator);
        }
        // Performance boost
        if (typeguard === ANY || typeguard === UNKNOWN) {
            return;
        }
        for (let i = 0; i < value.length; i++) {
            typeguard(`${name}[${i}]`, value[i], validator);
        }
    };
    DEBUG: fn.explain = `ARRAY(${typeguard.explain ?? typeguard.name})`;
    return fn;
}
/**
 * @template {readonly Typeguard<any>[]} T
 * @param {T} assertions
 * returns {Assertion<keyof { -readonly [P in keyof T]: Asserted<T[P]>}>}
 * @returns {Assertion<Asserted<T[number]>>}
 */
export function OR(...assertions) {
    /** @type {Typeguard<Asserted<T[number]>>} */
    const assertion = (name, value, validator) => {
        /** @type {Validator} */
        const childValidator = { [CAPTURE]: true };
        let count = assertions.length;
        /** @ŧype {string[]} */
        const messages = [];
        for (const assertion of assertions) {
            try {
                assertion(name, value, childValidator);
            }
            catch (reason) {
                count--;
                messages.push(isInstanceOf(reason, Error) ? reason.message : "unknown (broken validator)");
            }
        }
        if (!count) {
            fail(messages.join("\n"), value, validator);
        }
    };
    DEBUG: assertion.explain = `OR(${assertions.map((a) => a.explain ?? a.name).join(", ")})`;
    return assertion;
}
/**
 * @template U
 * @typedef {(U extends any ? (x: U)=>void : never) extends ((x: infer I)=>void) ? I : never} UnionToIntersection
 */
/**
 * @template {readonly Typeguard<any>[]} T
 * @param {T} assertions
 * returns {Assertion<keyof { -readonly [P in keyof T]: Asserted<T[P]>}>}
 * @returns {Typeguard<UnionToIntersection<Asserted<T[number]>>>}
 */
export function AND(...assertions) {
    /**
     * @type {Typeguard<UnionToIntersection<Asserted<T[number]>>>}
     */
    const assertion = (name, value, validator) => {
        /** @type {Validator} */
        const childValidator = { [CAPTURE]: true };
        /** @type {string[]} */
        const validKeys = [];
        /** @type {string[]} */
        const excessKeys = [];
        let hasExcessKeys = true;
        for (const assertion of assertions) {
            try {
                assertion(name, value, childValidator);
                hasExcessKeys = false;
            }
            catch (reason) {
                if (reason instanceof ExcessKeys) {
                    validKeys.push(...reason.valid);
                    excessKeys.push(...reason.excess);
                    continue;
                }
                fail(isInstanceOf(reason, Error) ? reason.message : `${name} unknown (broken validator)`, value, validator);
            }
        }
        if (excessKeys.length && hasExcessKeys) {
            const finalExcessKeys = excessKeys
                .filter((k) => !validKeys.includes(k))
                .filter((k, i, a) => a.indexOf(k) === i);
            if (finalExcessKeys.length) {
                const error = new ExcessKeys(name, [], finalExcessKeys);
                fail(error.message, value, validator);
            }
        }
    };
    DEBUG: assertion.explain = `AND(${assertions.map((a) => a.explain ?? a.name).join(", ")})`;
    return assertion;
}
/**
 * @template {readonly Assertion<any>[]} T
 * @param {T} assertions
 * @returns {Assertion< { -readonly [P in keyof T]: Asserted<T[P]>} >}
 */
export function TUPLE(...assertions) {
    return (name, value, validator) => {
        typeguard(name, value, ARRAY(ANY), validator);
        if (!Array.isArray(value)) {
            return;
        }
        for (let i = 0; i < value.length; i++) {
            typeguard(`${name}[${i}]`, value[i], assertions[i], validator);
        }
    };
}
/**
 * @template {readonly any[]} T
 * @param {T} options
 */
export function ENUM(options) {
    /** @type {Typeguard<T[number]>} */
    const assertion = (name, value, validator) => {
        for (const v of options) {
            if (value === v) {
                return;
            }
        }
        fail(`${name} not in ${JSON.stringify(options)}`, value, validator);
    };
    DEBUG: assertion.explain = `ENUM(${JSON.stringify(options)})`;
    return assertion;
}
class ExcessKeys extends Error {
    /**
     * @param {string} name
     * @param {string[]} valid
     * @param {string[]} excess
     */
    constructor(name, valid, excess) {
        super(`${name} has excess keys: ${JSON.stringify(excess)}`);
        this.valid = valid;
        this.excess = excess;
    }
}
/**
 * Validate object's own enumerable string properties.
 *
 * @template {string} K
 * @template V
 * @param {K extends string ? Typeguard<K> : never} keyAssertion
 * @param {Typeguard<V>} valueAssertion
 */
export function RECORD(keyAssertion, valueAssertion) {
    /** @type {Typeguard<{[key in K]: V}>} */
    const fn = (name, value, validator) => {
        if (isObject(value)) {
            const keyValidator = { [CAPTURE]: true };
            /** @type {string[]} */
            const validKeys = [];
            /** @type {string[]} */
            const excessKeys = [];
            for (const [key, item] of Object.entries(value)) {
                try {
                    keyAssertion(`${name} key "${key}"`, key, keyValidator);
                }
                catch (reason) {
                    excessKeys.push(key);
                    continue;
                }
                valueAssertion(`${name}["${key}"]`, item, validator);
                validKeys.push(key);
            }
            if (excessKeys.length) {
                const error = new ExcessKeys(name, validKeys, excessKeys);
                if (validator?.[CAPTURE]) {
                    throw error;
                }
                fail(error.message, value, validator);
            }
        }
        else {
            fail(`${name} is not an object`, value, validator);
        }
    };
    DEBUG: fn.explain = `RECORD(${keyAssertion.explain ?? keyAssertion.name}, ${valueAssertion.explain ?? valueAssertion.name})`;
    return fn;
}
/**
 * Validate object's own enumerable string properties.
 *
 * @template {Record<string, Typeguard<unknown>>} T
 * @param {T} asserts
 */
export function OBJECT(asserts) {
    /** @type {Typeguard<{[P in keyof T]: Asserted<T[P]>}>} */
    const fn = (name, value, validator) => {
        if (isObject(value)) {
            for (const key in asserts) {
                typeguard(`${name}["${key}"]`, 
                /** @type {Record<string, unknown>} */ (value)[key], asserts[key], validator);
            }
            const valid = Object.keys(asserts);
            const excess = Object.keys(value).filter((key) => !valid.includes(key));
            if (excess.length) {
                const error = new ExcessKeys(name, valid, excess);
                if (validator?.[CAPTURE]) {
                    throw error;
                }
                fail(error.message, { value, excess, valid }, validator);
            }
        }
        else {
            fail(`${name} is not an object`, value, validator);
        }
    };
    DEBUG: {
        const props = Object.entries(asserts)
            .map(([k, v]) => [k, v.explain ?? v.name])
            .reduce((res, cur) => `${res}${res ? ", " : ""}"${cur[0]}": ${cur[1]}`, "");
        fn.explain = `OBJECT({${props}})`;
    }
    return fn;
}
/**
 * @param {RegExp} regex
 * @returns {Assertion<string>}
 */
export function REGEX(regex) {
    return (name, value, validator) => {
        typeguard(name, value, STRING, validator);
        if (isString(value) && !regex.test(value)) {
            fail(`${name} does not match regular expression "${regex}".`, value, validator);
        }
    };
}
// ### ASSERTIONS ###
/**
 * @type {Assertion<any>}
 */
export function ANY() { }
/**
 * @type {Assertion<unknown>}
 */
export function UNKNOWN() { }
/**
 * @type {Assertion<never>}
 */
export function NEVER(name, value, validator) {
    fail(`${name} is invalid`, value, validator);
}
/**
 * @type {Assertion<number>}
 */
export function INTEGER(name, value, validator) {
    if (!(typeof value === "number") || !Number.isSafeInteger(value)) {
        fail(`${name} is not an integer`, value, validator);
    }
}
/**
 * @type {Assertion<number>}
 */
export function FLOAT(name, value, validator) {
    if (typeof value !== "number" || Number.isNaN(value)) {
        fail(`${name} is not a float`, value, validator);
    }
}
/**
 * @type {Assertion<string>}
 */
export function STRING(name, value, validator) {
    if (typeof value !== "string") {
        fail(`${name} is not a string`, value, validator);
    }
}
/**
 * @type {Assertion<boolean>}
 */
export function BOOLEAN(name, value, validator) {
    if (typeof value !== "boolean") {
        fail(`${name} is not a boolean`, value, validator);
    }
}
/**
 * @type {Assertion<null>}
 */
export function NULL(name, value, validator) {
    if (value !== null) {
        fail(`${name} is not null`, value, validator);
    }
}
/**
 * @type {Assertion<undefined>}
 */
export function UNDEFINED(name, value, validator) {
    if (value !== undefined) {
        fail(`${name} is not undefined`, value, validator);
    }
}
// ### Others ###
/**
 * @param {Object} target
 * @param {Record<string, unknown>} source
 */
export function FULLY_ASSIGNED(target, source) {
    for (const key of Object.getOwnPropertyNames(source)) {
        if (!Object.prototype.hasOwnProperty.call(target, key)) {
            fail("unassigned own property", { key, value: source[key], source, target });
        }
    }
}
/**
 * @param {Function} constructor
 * @param {Object} object
 */
export function ABSTRACT_CLASS(constructor, object) {
    if (object.constructor === constructor) {
        fail("instance of an abstract class", { constructor, object });
    }
}
/**
 * @param {Function} constructor
 * @param {Object} object
 */
export function FINAL_CLASS(constructor, object) {
    if (object.constructor !== constructor) {
        fail("instance of an extended final class", { constructor, object });
    }
}
